import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Row,
  Col,
  Label,
  Input,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { AdminService } from "services/AdminService";
import { Puff } from "react-loader-spinner";
import { Shift } from "models/Shift";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSuccess?: () => void;
  existingShift?: Shift | null; // Optional for edit mode
}

const AdminCreateShiftModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onSuccess,
  existingShift,
}) => {
  const isEditMode = !!existingShift;

  const [startDateTime, setStartDateTime] = useState<Date | null>(new Date());
  const [endDateTime, setEndDateTime] = useState<Date | null>(
    moment().add(4, "hours").toDate()
  );
  const [requiredDrivers, setRequiredDrivers] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ Prefill data when editing
  useEffect(() => {
    if (existingShift) {
      // Parse the date string as local time (ignoring timezone)
      const startLocal = new Date(existingShift.startDateTime.replace('Z', ''));
      const endLocal = new Date(existingShift.endDateTime.replace('Z', ''));
      
      setStartDateTime(startLocal);
      setEndDateTime(endLocal);
      setRequiredDrivers(existingShift.requiredDrivers);
    } else {
      setStartDateTime(new Date());
      setEndDateTime(moment().add(4, "hours").toDate());
      setRequiredDrivers(1);
    }
  }, [existingShift]);

  const validateForm = (): boolean => {
    if (!startDateTime || !endDateTime) {
      toast.error("Please select both start and end times", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return false;
    }
    if (moment(endDateTime).isBefore(moment(startDateTime))) {
      toast.error("End time cannot be earlier than start time", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return false;
    }
    if (requiredDrivers <= 0) {
      toast.error("Required drivers must be greater than 0", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return false;
    }
    return true;
  };

  // Helper function to format date as local datetime string
  const formatAsLocalDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    // Return in ISO format but without timezone (treat as local)
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    // Type guard to ensure dates are not null
    if (!startDateTime || !endDateTime) {
      toast.error("Please select both start and end times");
      return;
    }
    
    setIsLoading(true);

    try {
      // ✅ NEW: Store exactly what user selected (no timezone conversion)
      const payload = {
        _id: existingShift?._id,
        startDateTime: formatAsLocalDateTime(startDateTime), // Store as-is
        endDateTime: formatAsLocalDateTime(endDateTime),     // Store as-is
        requiredDrivers,
      };

      console.log("📤 Sending to backend:", payload);

      const response = isEditMode
        ? await AdminService.editShift(payload)
        : await AdminService.createShift(payload);

      if (response.success) {
        toast.success(
          isEditMode
            ? "Shift updated successfully!"
            : "Shift created successfully!",
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
        if (onSuccess) onSuccess();
        toggle();
      } else {
        toast.error(
          response.message ||
            (isEditMode
              ? "Failed to update shift"
              : "Failed to create shift"),
          { position: toast.POSITION.BOTTOM_RIGHT }
        );
      }
    } catch (error) {
      console.error("❌ Error saving shift:", error);
      toast.error("An error occurred while saving the shift", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>
        {isEditMode ? "Edit Shift" : "Create New Shift"}
      </ModalHeader>
      <ModalBody>
        <Form>
          <Row>
            <Col md="12" className="mb-3">
              <Label className="place-order-title">Start Date & Time</Label>
              <DatePicker
                selected={startDateTime}
                onChange={(date) => setStartDateTime(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="yyyy-MM-dd HH:mm"
                timeCaption="Time"
                className="form-control"
              />
            </Col>

            <Col md="12" className="mb-3">
              <Label className="place-order-title">End Date & Time</Label>
              <DatePicker
                selected={endDateTime}
                onChange={(date) => setEndDateTime(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="yyyy-MM-dd HH:mm"
                timeCaption="Time"
                className="form-control"
                minDate={startDateTime || new Date()}
              />
            </Col>

            <Col md="12" className="mb-3">
              <Label className="place-order-title">Required Drivers</Label>
              <Input
                type="number"
                value={requiredDrivers}
                onChange={(e) => setRequiredDrivers(Number(e.target.value))}
                min="1"
                className="form-control"
              />
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={toggle}>
          Cancel
        </Button>
        <Button
          color={isEditMode ? "warning" : "primary"}
          size="sm"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? (
            <Puff height={24} width={24} color="#fff" />
          ) : isEditMode ? (
            "Update Shift"
          ) : (
            "Create Shift"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AdminCreateShiftModal;
